import {Component} from 'react';
import {WithTranslation, withTranslation} from 'react-i18next';
import {
    CasingsCustomReportParameters,
    CasingsScheduledReportParameters,
    ReportPeriodType,
    ScheduledReportPeriod,
} from '../../../../../../store/reports';
import styles from './casings.module.scss';
import ScheduledPeriodSelectorComponent from '../../components/scheduled-period-selector.component';
import {
    ServiceProviderSelection,
} from '../../../../../../store/analytics';
import DateRangeSelectorComponent from '../../../../components/date-range-selector.component';
import {ServiceProviderGroupWithConfiguration} from '../../../../../../store/service-provider-groups';
import {ServiceProviderSelectionsControlValue} from '../../../../../analytics/dashboard-controls/service-providers-selector/types/service-providers-selections-control-value';
import ServiceProvidersSelectorComponent from '../../../../components/service-providers-selector.component';

class CasingsComponent extends Component<AllProps, AllState> {

    constructor(props) {
        super(props);
        const {parameters} = this.props;

        let period: ScheduledReportPeriod = ScheduledReportPeriod.PAST_MONTH;
        let serviceProviders: ServiceProviderSelection = {includes: []};
        if (parameters && parameters.serviceProviders) {
            serviceProviders = parameters.serviceProviders;
        }
        if (parameters && parameters.period) {
            period = parameters.period;
        }

        this.state = {
            period,
            serviceProviderSelection: new ServiceProviderSelectionsControlValue('', serviceProviders),
        };
    }

    public render(): JSX.Element {
        const {periodType, serviceProviderGroup, t} = this.props;
        const {period, serviceProviderSelection} = this.state;

        return (
            <div className={styles.content}>
                {periodType === ReportPeriodType.Scheduled
                    ? <>
                        <div className={styles.title}>{t('Period')}</div>
                        <ScheduledPeriodSelectorComponent
                            defaultPeriod={period}
                            onPeriodChange={(p) => this.onPeriodChanged(p)}
                        />
                        <div className={styles.title}>{t('Parameters')}</div>
                    </>
                    : null
                }
                <div className={styles.form}>
                    <div className={styles.formRow}>
                        <div className={styles.info}>{t('Select a service provider')}</div>
                        <ServiceProvidersSelectorComponent
                            defaultServiceProviderSelection={serviceProviderSelection && serviceProviderSelection.value.includes.length > 0 ? serviceProviderSelection : undefined}
                            onServiceProviderSelectionChange={(controlValue) => this.onServiceProviderSelectionChanged(controlValue)}
                        />
                    </div>
                    {periodType === ReportPeriodType.Custom
                        ? <div className={styles.formRow}>
                            <div className={styles.formColumn}>
                                <div className={styles.info}>{t('Specify a period')}</div>
                                <DateRangeSelectorComponent
                                    earliestDate={serviceProviderGroup?.earliestContractStartDate}
                                    latestDate={new Date()}
                                    fromDateChanged={(date => this.onFromDateChanged(date))}
                                    toDateChanged={(date => this.onToDateChanged(date))}
                                />
                            </div>
                        </div>
                        : null
                    }
                </div>
            </div>
        );
    }

    private onServiceProviderSelectionChanged(controlValue: ServiceProviderSelectionsControlValue): void {
        const {periodType, onCustomParametersChanged, onScheduledParametersChanged} = this.props;
        const {serviceProviderSelection} = this.state;

        if (!serviceProviderSelection || (serviceProviderSelection && !controlValue.equals(serviceProviderSelection))) {
            this.setState({
                serviceProviderSelection: controlValue,
            },()=>{
                // Make sure previous state has been applied, as service provider and sold to selection changes will trigger at the same time
                const { period, fromDate, toDate} = this.state;
                if (periodType === ReportPeriodType.Custom) {
                    onCustomParametersChanged({
                        serviceProviders: controlValue,
                        fromDate,
                        toDate,
                    });
                }
                if (periodType === ReportPeriodType.Scheduled) {
                    onScheduledParametersChanged({
                        period,
                        serviceProviders: controlValue.value,
                    });
                }
            });
        }
    }

    private onPeriodChanged(period: ScheduledReportPeriod) {
        const {onScheduledParametersChanged} = this.props;
        const {serviceProviderSelection} = this.state;

        onScheduledParametersChanged({
            period,
            serviceProviders: serviceProviderSelection?.value,
        });

        this.setState({
            period,
        });
    }

    private onFromDateChanged(fromDate?: Date) {
        const {onCustomParametersChanged} = this.props;

        this.setState({
            fromDate,
        }, () => {
            // Use callback and fresh state because from & to date might change simultaneously
            const {serviceProviderSelection, toDate} = this.state;

            onCustomParametersChanged({
                serviceProviders: serviceProviderSelection,
                fromDate,
                toDate,
            });
        });
    }

    private onToDateChanged(toDate?: Date) {
        const {onCustomParametersChanged} = this.props;

        this.setState({
            toDate,
        }, () => {
            // Use callback and fresh state because from & to date might change simultaneously
            const {serviceProviderSelection, fromDate} = this.state;

            onCustomParametersChanged({
                serviceProviders: serviceProviderSelection,
                fromDate,
                toDate,
            });
        });
    }
}

export default withTranslation()(CasingsComponent);

interface OwnProps {
    periodType: ReportPeriodType;
    serviceProviderGroup?: ServiceProviderGroupWithConfiguration;
    parameters?: CasingsScheduledReportParameters;
    onCustomParametersChanged: (parameters: CasingsCustomReportParameters) => void;
    onScheduledParametersChanged: (parameters: CasingsScheduledReportParameters) => void;
}

type AllProps = WithTranslation & OwnProps;

interface OwnState {
    period: ScheduledReportPeriod;
    serviceProviderSelection?: ServiceProviderSelectionsControlValue;
    fromDate?: Date;
    toDate?: Date;
}

type AllState = OwnState;
